import React, { useEffect, useState } from 'react';
import { Star, MessageSquare, Eye, EyeOff, Award, Send } from 'lucide-react';
import { api } from '../../services/apiClient';

interface Review {
  id: number;
  rating: number;
  title: string | null;
  content: string | null;
  is_published: boolean;
  is_featured: boolean;
  admin_response: string | null;
  admin_responded_at: string | null;
  created_at: string;
  route_name: string | null;
  guide_first_name: string | null;
  guide_last_name: string | null;
}

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} size={14} className={i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
    ))}
  </div>
);

export const TripReviewsPanel: React.FC<{ applicationId: number; email?: string }> = ({ applicationId, email }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [respondingId, setRespondingId] = useState<number | null>(null);
  const [responseText, setResponseText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/wycieczki/application/${applicationId}/reviews`)
      .then(r => setReviews(r.data.reviews || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [applicationId]);

  const togglePublish = async (review: Review) => {
    await api.patch(`/wycieczki/reviews/${review.id}`, { is_published: !review.is_published });
    load();
  };

  const toggleFeatured = async (review: Review) => {
    await api.patch(`/wycieczki/reviews/${review.id}`, { is_featured: !review.is_featured });
    load();
  };

  const submitResponse = async (reviewId: number) => {
    if (!responseText.trim()) return;
    setSaving(true);
    await api.patch(`/wycieczki/reviews/${reviewId}`, { admin_response: responseText.trim() });
    setRespondingId(null);
    setResponseText('');
    setSaving(false);
    load();
  };

  if (loading) return null;
  if (reviews.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 flex items-center gap-2">
        <Star size={18} className="text-white fill-white" />
        <h3 className="text-white font-bold text-sm">Recenzje klienta ({reviews.length})</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {reviews.map(review => {
          const guide = [review.guide_first_name, review.guide_last_name].filter(Boolean).join(' ');
          return (
            <div key={review.id} className="px-5 py-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Stars rating={review.rating} />
                    {review.is_featured && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Wyroznienie</span>}
                    {!review.is_published && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Ukryta</span>}
                  </div>
                  {review.title && <p className="font-semibold text-sm text-gray-800">{review.title}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => togglePublish(review)} className={`p-1.5 rounded-lg hover:bg-gray-100 ${review.is_published ? 'text-green-600' : 'text-gray-400'}`} title={review.is_published ? 'Ukryj' : 'Opublikuj'}>
                    {review.is_published ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button onClick={() => toggleFeatured(review)} className={`p-1.5 rounded-lg hover:bg-gray-100 ${review.is_featured ? 'text-amber-500' : 'text-gray-400'}`} title={review.is_featured ? 'Usuń wyroznienie' : 'Wyroznij'}>
                    <Award size={16} />
                  </button>
                  <button onClick={() => { setRespondingId(review.id); setResponseText(review.admin_response || ''); }} className="p-1.5 rounded-lg hover:bg-gray-100 text-blue-500" title="Odpowiedz">
                    <MessageSquare size={16} />
                  </button>
                </div>
              </div>

              {review.content && <p className="text-sm text-gray-600 mb-2 leading-relaxed">{review.content}</p>}

              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span>{new Date(review.created_at).toLocaleDateString('pl-PL')}</span>
                {review.route_name && <span>{review.route_name}</span>}
                {guide && <span>Przewodnik: {guide}</span>}
              </div>

              {/* Admin response */}
              {review.admin_response && respondingId !== review.id && (
                <div className="mt-3 bg-blue-50 rounded-lg p-3 border-l-3 border-blue-400">
                  <p className="text-xs font-medium text-blue-700 mb-1">Odpowiedz administratora:</p>
                  <p className="text-sm text-blue-800">{review.admin_response}</p>
                  {review.admin_responded_at && (
                    <p className="text-xs text-blue-400 mt-1">{new Date(review.admin_responded_at).toLocaleDateString('pl-PL')}</p>
                  )}
                </div>
              )}

              {/* Response form */}
              {respondingId === review.id && (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={responseText}
                    onChange={e => setResponseText(e.target.value)}
                    placeholder="Napisz odpowiedz na recenzje..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => submitResponse(review.id)}
                      disabled={saving || !responseText.trim()}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Send size={14} />
                      {saving ? 'Zapisywanie...' : 'Zapisz odpowiedz'}
                    </button>
                    <button onClick={() => setRespondingId(null)} className="px-3 py-1.5 text-gray-500 text-sm hover:text-gray-700">
                      Anuluj
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
